import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import ContributeWriter from "./ContributeWriter";
import { api, mediaUrl } from "../../lib/api";

const fmtDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }).toUpperCase() : "";

export default function Read() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    api.getArticles().then(setArticles).catch(() => {});
  }, []);

  const categories = useMemo(() => ["ALL", ...new Set(articles.map((a) => a.category))], [articles]);
  const featured = articles.find((a) => a.featured);
  const rest = articles.filter((a) => !a.featured && (filter === "ALL" || a.category === filter));
  const showFeatured = featured && (filter === "ALL" || featured.category === filter);

  return (
    <section id="read" className="relative py-24 md:py-36 bg-obsidian" data-testid="read-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Eyebrow index="07" label="EDITORIAL" />
          <h2 className="font-display text-cream text-6xl md:text-8xl tracking-tight leading-[0.85]" data-testid="read-heading">
            THE <span className="text-burnt">EDITORIAL.</span>
          </h2>
          <p className="mt-5 max-w-lg text-sand font-light">Culture, music, business and commentary — written from inside the room.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="read-category-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`read-filter-${c.toLowerCase()}`}
                className={`font-mono text-[10px] tracking-[0.2em] px-4 py-2 rounded-full border transition-all duration-300 ${
                  filter === c ? "bg-burnt border-burnt text-cream" : "border-woodborder text-clay hover:border-tan/50 hover:text-tan"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <span className="h-px w-8 bg-woodborder" />
            <ContributeWriter variant="inline" />
          </div>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {showFeatured && (
            <Reveal className="md:col-span-2 lg:row-span-2">
              <Link to={`/read/${featured.slug}`} className="group block h-full" data-testid="read-featured-article">
                <div className="relative h-full min-h-[380px] lg:min-h-[520px] overflow-hidden border border-woodborder">
                  <img
                    src={mediaUrl(featured.image)} alt={featured.title} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover grayscale-[0.4] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
                  <div className="absolute bottom-0 p-7 md:p-10">
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                      <span className="font-mono text-[10px] tracking-[0.2em] bg-burnt text-cream px-3 py-1.5">{featured.category}</span>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-tan">{featured.read_time}</span>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-clay">{fmtDay(featured.published_at)}</span>
                    </div>
                    <h3 className="font-editorial font-extrabold text-cream text-2xl md:text-4xl leading-tight max-w-xl group-hover:text-tan-light transition-colors">
                      {featured.title}
                    </h3>
                    <p className="mt-3 text-sand text-sm max-w-lg font-light hidden md:block">{featured.excerpt}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          )}
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={0.06 * i}>
              <Link to={`/read/${a.slug}`} className="group block" data-testid={`read-article-card-${i}`}>
                <div className="relative overflow-hidden border border-woodborder">
                  <img
                    src={mediaUrl(a.image)} alt={a.title} loading="lazy"
                    className="w-full aspect-[4/3] object-cover grayscale-[0.4] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                  <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] bg-obsidian/80 backdrop-blur text-tan px-3 py-1.5 border border-tan/20">
                    {a.category}
                  </span>
                </div>
                <div className="pt-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-editorial font-bold text-cream text-lg leading-snug group-hover:text-tan-light transition-colors">{a.title}</h3>
                    <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-clay">
                      {a.read_time} — {fmtDay(a.published_at)}
                    </p>
                  </div>
                  <ArrowUpRight size={18} className="text-clay group-hover:text-burnt transition-colors shrink-0 mt-1" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
